import React, { useEffect, useState } from "react";

type SortType = "asc" | "desc";

export const useTableSort = <T>(fields: T[], orderDefaultBy: string) => {
    const [sortedFields, setSortedFields] = useState<T[]>([]);
    const [order, setOrder] = React.useState<SortType>("asc");
    const [orderBy, setOrderBy] = React.useState(orderDefaultBy);

    const handleRequestSort = (property: string) => () => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const stableSort = <T>(array: T[], comparator: (a: T, b: T) => number) => {
        const stabilizedThis = array.map((element, index) => {
            return {
                element,
                index,
            };
        });

        stabilizedThis.sort((a, b) => {
            const order = comparator(a.element, b.element);
            if (order !== 0) {
                return order;
            }

            return a.index - b.index;
        });

        return stabilizedThis.map(({ element }) => element);
    };

    const descendingComparator = <T>(a: { [key: string]: T }, b: { [key: string]: T }, orderBy: string): number => {
        if (b[orderBy] < a[orderBy]) {
            return -1;
        }

        if (b[orderBy] > a[orderBy]) {
            return 1;
        }

        return 0;
    };

    useEffect(() => {
        const getComparator = (order: SortType, orderBy: string) => {
            return order === "desc"
                ? <T>(a: { [key: string]: T }, b: { [key: string]: T }) => descendingComparator(a, b, orderBy)
                : <T>(a: { [key: string]: T }, b: { [key: string]: T }) => -descendingComparator(a, b, orderBy);
        };

        // eslint-disable-next-line
        setSortedFields(stableSort(fields, getComparator(order, orderBy) as any));
    }, [orderBy, fields, order]);

    return {
        sortedFields,
        order,
        orderBy,
        toggleSort: handleRequestSort,
    };
};
